import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore, PlayArrow } from '@mui/icons-material';
import { brandColors } from '../../theme';

interface ReportSection {
  title: string;
  description: string;
  items: { name: string; description: string }[];
}

const reportSections: ReportSection[] = [
  {
    title: 'Market Overview & Economic Context',
    description: "Comprehensive analysis of the destination's economic landscape and tourism fundamentals",
    items: [
      {
        name: 'Destination Overview',
        description: "In-depth look at the market's positioning",
      },
      {
        name: 'Economic Overview',
        description: 'Key economic indicators and trends',
      },
      {
        name: 'Access & Infrastructure',
        description: 'Transportation and connectivity analysis',
      },
    ],
  },
  {
    title: 'Hotel Market Analysis',
    description: 'Detailed examination of supply, demand, and performance metrics',
    items: [
      {
        name: 'Hotel Supply Overview',
        description: 'Current inventory and market segmentation',
      },
      {
        name: 'Hotel Demand Segmentation',
        description: 'Guest profiles and source markets',
      },
      {
        name: 'Market Performance Analysis',
        description: 'RevPAR, ADR, and occupancy trends',
      },
      {
        name: 'Seasonality Patterns',
        description: 'Monthly and seasonal variations',
      },
    ],
  },
  {
    title: 'Demand Drivers & Market Dynamics',
    description: 'Key factors influencing hotel demand and market growth',
    items: [
      {
        name: 'Tourism Infrastructure',
        description: 'Attractions and visitor amenities',
      },
      {
        name: 'Key Demand Generators',
        description: 'Business sectors and visitor segments',
      },
      {
        name: 'Meeting & Event Facilities',
        description: 'MICE infrastructure assessment',
      },
      {
        name: 'Major Events Calendar',
        description: 'Annual events and their impact',
      },
    ],
  },
  {
    title: 'Strategic Insights & Future Outlook',
    description: 'Forward-looking analysis and investment opportunities',
    items: [
      {
        name: 'New Supply Pipeline',
        description: 'Upcoming hotel developments',
      },
      {
        name: 'Market Opportunities',
        description: 'Growth potential and gaps',
      },
      {
        name: 'Investment Climate',
        description: 'Market attractiveness for investors',
      },
      {
        name: 'SWOT Analysis',
        description: 'Strategic market assessment',
      },
      {
        name: 'Market Outlook & Forecast',
        description: '5-year projections and scenarios',
      },
    ],
  },
];

export const ReportContents: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>('panel0');

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: brandColors.lightGray,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: brandColors.indigo,
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            What's Included in Every MarketCompass Report
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: brandColors.mediumGray,
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            17 comprehensive sections covering every aspect of hotel market analysis
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          {reportSections.map((section, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{
                mb: 2,
                borderRadius: 2,
                boxShadow: expanded === `panel${index}` 
                  ? `0 4px 16px rgba(0,0,0,0.1), 0 0 0 2px ${brandColors.gold}`
                  : '0 1px 3px rgba(0,0,0,0.1)',
                '&:before': {
                  display: 'none',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: brandColors.indigo }} />}
                sx={{
                  py: 2,
                  px: 3,
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <PlayArrow
                    sx={{
                      color: brandColors.gold,
                      mr: 2,
                      transform: expanded === `panel${index}` ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: brandColors.indigo,
                      fontWeight: 600,
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: brandColors.mediumGray,
                    mb: 3,
                    pl: 5,
                  }}
                >
                  {section.description}
                </Typography>
                
                <List sx={{ pl: 3 }}>
                  {section.items.map((item, itemIndex) => (
                    <ListItem
                      key={itemIndex}
                      sx={{
                        py: 1,
                        px: 2,
                        '&:hover': {
                          backgroundColor: `${brandColors.gold}10`,
                          borderRadius: 1,
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: brandColors.indigo,
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            • {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              color: brandColors.mediumGray,
                              pl: 2,
                            }}
                          >
                            {item.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
};